import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
} from "react";
import { BarItem, BarItemProps } from "@nivo/bar";
import { animated, SpringValue, to, useSpring } from "@react-spring/web";
import { buildClasses } from "../utils/buildClasses";
import {
  GROUPED_BAR_MOTION,
  GroupedBarChart,
  GroupedBarDatum,
  useGroupedBars,
} from "../common/GroupedBarChart";
import { RyeChartTooltip } from "../rye/RyeChartTooltip";
import { DollarAmount, useFormatDollarAmount } from "../common/DollarAmount";
import {
  aheadOfPace,
  BudgetBarColor,
  BudgetBarGroup,
  budgetBarLengths,
  BudgetBarRow,
  BudgetBarScale,
  budgetLeft,
} from "./budgetBarRows";

// Hex values from the Tailwind tokens in tailwind.config.js; Nivo needs real
// colors, not class names. The band is the budget, a shade deeper as far as
// the budget comes to by now, and the bar inside it what actually happened.
// Group bars are a shade darker than their categories'. Each type's band is
// its 200 stop a quarter of the way to 100, toDate halfway from 200 to 300,
// category 500, and group 600.
const COLORS: Record<
  BudgetBarColor,
  { band: string; toDate: string; category: string; group: string }
> = {
  // blue (sky)
  expenses: {
    band: "#C4E9FD",
    toDate: "#9CDCFC",
    category: "#0ea5e9",
    group: "#0284c7",
  },
  // green
  income: {
    band: "#BBEFD5",
    toDate: "#9FE8C4",
    category: "#3FD088",
    group: "#2AAC6B",
  },
  // purple
  savings: {
    band: "#DDD9FF",
    toDate: "#CAC3FF",
    category: "#7d57ff",
    group: "#6930f7",
  },
  // yellow, matching the unreviewed indicator
  unreviewed: {
    band: "#FFECA9",
    toDate: "#FFE792",
    category: "#FECF21",
    group: "#FECF21",
  },
};
const NO_COLOR = "#cbd5e1"; // on-surface-300
const NUMBER_TEXT_COLOR = "#475569"; // on-surface-600
const OVERSPENT_TEXT_COLOR = "#e11d48"; // red-600
const BAND_RADIUS_PX = 3;
// Faded, for the rows that aren't selected.
const UNSELECTED_OPACITY = 0.35;
// The actual bar's thickness, as a fraction of the budget band's.
const ACTUAL_THICKNESS = 0.5;

const COLUMN_WIDTH_PX = 92;
const COLUMN_GAP_PX = 16;
const COLUMNS = ["Actual", "Budget", "Remaining"] as const;
const MARGIN_RIGHT_PX = COLUMN_GAP_PX + COLUMNS.length * COLUMN_WIDTH_PX;

// Nivo's own bar stays underneath, for its hover, tooltip, and click
// handling, but the shapes drawn over it are what show.
const TRANSPARENT = new SpringValue("transparent");

function barColor(row: BudgetBarRow, part: "band" | "toDate" | "actual") {
  if (!row.color) return NO_COLOR;
  const colors = COLORS[row.color];
  return part === "actual" ? colors[row.kind] : colors[part];
}

/** What the bar and tooltip components need beyond the rows themselves. */
const BudgetBarsContext = createContext<{ scale: BudgetBarScale }>({
  scale: "dollars",
});

/**
 * A bullet bar: the budget as a light band, the actual amount as a thinner
 * bar inside it. The band is a shade deeper as far as the budget says the row
 * should have reached by now. An overrun is the same bar running on past the
 * end of the band. Then the row's numbers, in fixed columns at the right.
 *
 * Every length is a fraction of the width Nivo animates the bar to, so the
 * shapes grow, shrink, and rescale together with it.
 */
function BulletBar(props: BarItemProps<GroupedBarDatum>): JSX.Element {
  const { allRowsById, expandedGroupIds, selectedId, innerWidth } =
    useGroupedBars<BudgetBarRow>();
  const { scale } = useContext(BudgetBarsContext);
  const formatDollarAmount = useFormatDollarAmount();
  const rowId = String(props.bar.data.indexValue);
  const row = allRowsById.get(rowId);
  const format = (amount: number, signed: boolean) =>
    formatDollarAmount(amount, {
      currencyCode: row?.currency,
      showDollarSign: true,
      showPlus: signed,
      showMinus: signed,
    });
  const bandClipId = useId();
  const { transform, width, height, labelOpacity } = props.style;
  // An expanded group's bar shrinks away and its numbers fade with it; its
  // categories show the same money in detail.
  // Rows other than the selected one fade back, on the same spring Nivo uses
  // to fade other charts' bars by color.
  const { shownOpacity, selectionOpacity } = useSpring({
    shownOpacity: expandedGroupIds.has(rowId) ? 0 : 1,
    selectionOpacity:
      selectedId !== null && selectedId !== rowId ? UNSELECTED_OPACITY : 1,
    config: GROUPED_BAR_MOTION,
  });

  const bar = (
    <BarItem {...props} style={{ ...props.style, color: TRANSPARENT }} />
  );
  if (!row) return bar;

  const lengths = budgetBarLengths(row, scale);
  const of = (length: number) =>
    to(width, (w) =>
      lengths.value > 0 ? (Math.max(w, 0) * length) / lengths.value : 0,
    );
  const thinY = to(height, (h) => (h * (1 - ACTUAL_THICKNESS)) / 2);
  const thinHeight = to(height, (h) => h * ACTUAL_THICKNESS);
  const left = budgetLeft(row);
  // An expense budget is a limit, so what's under it is left and what's past
  // it is overspending; an income or savings budget is a goal, so what's
  // under it is still to go and what's past it is a good thing.
  const isLimit = row.budget < 0;
  const overspent = isLimit && left !== null && left < 0;
  const remainingWord =
    left !== null && left < 0
      ? isLimit
        ? "over"
        : "extra"
      : isLimit
        ? "left"
        : "to go";
  const columnX = (index: number) =>
    innerWidth + COLUMN_GAP_PX + (index + 1) * COLUMN_WIDTH_PX;

  return (
    <>
      {bar}
      <animated.g
        transform={transform}
        opacity={to(
          [labelOpacity, shownOpacity, selectionOpacity],
          (a, b, c) => a * b * c,
        )}
        style={{ pointerEvents: "none" }}
      >
        {lengths.budget > 0 && (
          <>
            <clipPath id={bandClipId}>
              <animated.rect
                width={of(lengths.budget)}
                height={height}
                rx={BAND_RADIUS_PX}
              />
            </clipPath>
            <g clipPath={`url(#${bandClipId})`}>
              <animated.rect
                width={of(lengths.budget)}
                height={height}
                fill={barColor(row, "band")}
              />
              <animated.rect
                width={of(lengths.pace)}
                height={height}
                fill={barColor(row, "toDate")}
              />
            </g>
          </>
        )}
        <animated.rect
          y={thinY}
          width={of(lengths.actual)}
          height={thinHeight}
          rx={2}
          fill={barColor(row, "actual")}
        />
        <g
          style={{
            fontFamily: "InterVariable, sans-serif",
            fontSize: 12,
            fill: NUMBER_TEXT_COLOR,
            fontWeight: row.kind === "group" ? 500 : 400,
          }}
        >
          {[
            format(row.actual, true),
            row.hasBudget ? format(row.budget, true) : "—",
            left === null
              ? "—"
              : `${format(Math.abs(left), false)} ${remainingWord}`,
          ].map((text, index) => (
            <animated.text
              key={COLUMNS[index]}
              fill={
                COLUMNS[index] === "Remaining" && overspent
                  ? OVERSPENT_TEXT_COLOR
                  : undefined
              }
              x={columnX(index)}
              y={to(height, (h) => h / 2)}
              textAnchor="end"
              dominantBaseline="central"
            >
              {text}
            </animated.text>
          ))}
        </g>
      </animated.g>
    </>
  );
}

function BudgetTooltip({ row }: { row: BudgetBarRow }): JSX.Element {
  const pace = aheadOfPace(row);
  const formatDollarAmount = useFormatDollarAmount();
  const amount = (n: number) => (
    <DollarAmount
      n={n}
      size="sm"
      showDollarSign={true}
      showMinus={n < 0}
      showPlus={n > 0}
      align="right"
      currencyCode={row.currency}
    />
  );
  return (
    <RyeChartTooltip>
      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "gap-1.5",
          "bg-surface",
          "border",
          "border-surface-600",
          "shadow-glow",
          "rounded-md",
          "px-3",
          "py-2",
          "text-sm",
          "text-on-surface-700",
        )}
      >
        <span className={buildClasses("font-medium")}>{row.label}</span>
        <div
          className={buildClasses(
            "grid",
            "grid-cols-[auto_auto]",
            "gap-x-4",
            "gap-y-0.5",
          )}
        >
          <span className={buildClasses("text-on-surface-500")}>Actual</span>
          {amount(row.actual)}
          {row.hasBudget && (
            <>
              <span className={buildClasses("text-on-surface-500")}>
                Budgeted to date
              </span>
              {amount(row.budgetedToDate)}
              <span className={buildClasses("text-on-surface-500")}>
                Total budgeted
              </span>
              {amount(row.budget)}
            </>
          )}
        </div>
        {pace !== null && (
          <span className={buildClasses("text-xs", "text-on-surface-500")}>
            {Math.round(pace) === 0
              ? "Right on pace"
              : `${formatDollarAmount(Math.abs(pace), { currencyCode: row.currency, showDollarSign: true })} ${
                  pace > 0 ? "more" : "less"
                } than budgeted to date`}
          </span>
        )}
      </div>
    </RyeChartTooltip>
  );
}

/**
 * Budget tracking as grouped bullet bars: each row's budget, what actually
 * happened, and where the budget says it should be by now, with its numbers
 * in columns at the right.
 */
export function BudgetBars({
  groups,
  expandedGroupIds,
  onToggleGroup,
  scale,
  currency,
  selectedId,
  onSelectRow,
  headerStart,
}: {
  groups: BudgetBarGroup[];
  expandedGroupIds: ReadonlySet<string>;
  onToggleGroup: (groupId: string) => void;
  scale: BudgetBarScale;
  currency: string;
  selectedId: string | null;
  onSelectRow: (row: BudgetBarRow) => void;
  /** Shown at the start of the column headings, above the row labels. */
  headerStart?: ReactNode;
}): JSX.Element {
  const getValue = useCallback(
    (row: BudgetBarRow) =>
      row.kind === "group" && expandedGroupIds.has(row.id)
        ? 0
        : budgetBarLengths(row, scale).value,
    [expandedGroupIds, scale],
  );
  const formatDollarAmount = useFormatDollarAmount();
  const formatAxisValue = (value: number) =>
    scale === "budget"
      ? `${Math.round(value * 100)}%`
      : formatDollarAmount(value, {
          currencyCode: currency,
          showDollarSign: true,
          showMinus: true,
          compact: true,
        });
  const context = useMemo(() => ({ scale }), [scale]);

  return (
    <div className={buildClasses("flex", "flex-col")}>
      <div
        className={buildClasses(
          "flex",
          "items-end",
          "justify-end",
          "text-xs",
          "font-medium",
          "text-on-surface-500",
        )}
      >
        {headerStart && (
          <div className={buildClasses("mr-auto", "-ml-2")}>{headerStart}</div>
        )}
        {COLUMNS.map((column) => (
          <span
            key={column}
            className={buildClasses("text-right")}
            style={{ width: `${COLUMN_WIDTH_PX}px` }}
          >
            {column}
          </span>
        ))}
      </div>
      <BudgetBarsContext.Provider value={context}>
        <GroupedBarChart<BudgetBarRow>
          groups={groups}
          expandedGroupIds={expandedGroupIds}
          onToggleGroup={onToggleGroup}
          selectedId={selectedId}
          onSelectRow={onSelectRow}
          getValue={getValue}
          getColor={(row) => barColor(row, "actual")}
          barContent={BulletBar}
          tooltip={BudgetTooltip}
          formatAxisValue={formatAxisValue}
          marginRight={MARGIN_RIGHT_PX}
          ariaLabel="Budget, actual, and pace by category group and category"
        />
      </BudgetBarsContext.Provider>
    </div>
  );
}
